/**
 * GENERATION SCREENING ENVELOPE ADMISSIONS — the solver-integrity gate between
 * the live Firestore `equipment_catalog` and the G&I screening solver.
 *
 * DESIGN (solver integrity is the highest priority):
 *  - Firestore governs AVAILABILITY: which screening envelopes are OFFERED.
 *    Admins hide an envelope org-wide by soft-deleting its row (`active:false`)
 *    or deleting it — no code release needed.
 *  - The shipped, versioned static catalog stays the NUMERIC AUTHORITY: an
 *    admitted row resolves to the static `GenerationEquipmentCatalogEntry`
 *    matched by `generationScreeningId` + `generationCatalogVersion`. A
 *    Firestore edit can therefore never silently change solver duty values —
 *    at worst it changes which envelopes are offered, visibly.
 *  - Version drift or an unknown id → the row is REJECTED WITH A REASON that
 *    the UI surfaces. Nothing degrades silently.
 *  - No screening rows at all (unseeded project, offline, fetch failure) →
 *    authority 'static' and the full shipped catalog — byte-identical to the
 *    pre-Firestore behavior.
 *
 * Values applied to a scenario are still pinned into the scenario input at
 * apply time (`GenerationEquipmentBasis` embeds catalogVersion + rating
 * values), so saved scenarios remain immune to any later catalog change.
 *
 * Pure + deterministic: same docs → same result.
 */
import {
  GENERATION_EQUIPMENT_CATALOG,
  type GenerationEquipmentCatalogEntry,
} from './generationEquipmentCatalog';
import { GENERATION_EQUIPMENT_CATALOG_VERSION } from '../../types/generationImpact';

/** The raw shape read off an `equipment_catalog` screening doc. Untyped
 *  unknowns on purpose — Firestore data is untrusted input here. */
export interface ScreeningCatalogDocLike {
  docId: string;
  active?: unknown;
  generationScreeningId?: unknown;
  generationCatalogVersion?: unknown;
}

export interface ScreeningAdmissionRejection {
  docId: string;
  reason: string;
}

export interface ScreeningAdmissionResult {
  /** Envelopes to OFFER, in shipped-catalog order (static values authority). */
  admitted: readonly GenerationEquipmentCatalogEntry[];
  /** Rows present but not admitted — each with a user-facing reason. */
  rejected: readonly ScreeningAdmissionRejection[];
  /** Rows deliberately hidden by an admin (`active: false`). Not rejections. */
  hiddenCount: number;
  /** 'firestore' when ≥1 screening row exists (the DB governs availability);
   *  'static' when none do (unseeded/offline → full shipped catalog). */
  authority: 'firestore' | 'static';
}

const STATIC_BY_ID = new Map(
  GENERATION_EQUIPMENT_CATALOG.map((entry) => [entry.catalogId, entry]),
);

export function admitGenerationScreeningEntries(
  docs: readonly ScreeningCatalogDocLike[],
): ScreeningAdmissionResult {
  const screeningDocs = docs.filter(
    (d) => typeof d.generationScreeningId === 'string' && d.generationScreeningId.length > 0,
  );

  if (screeningDocs.length === 0) {
    return {
      admitted: [...GENERATION_EQUIPMENT_CATALOG],
      rejected: [],
      hiddenCount: 0,
      authority: 'static',
    };
  }

  const admittedIds = new Set<string>();
  const rejected: ScreeningAdmissionRejection[] = [];
  let hiddenCount = 0;

  for (const doc of screeningDocs) {
    const screeningId = doc.generationScreeningId as string;
    if (doc.active === false) {
      // Deliberate admin hide — excluded without a warning.
      hiddenCount += 1;
      continue;
    }
    const entry = STATIC_BY_ID.get(screeningId);
    if (!entry) {
      rejected.push({
        docId: doc.docId,
        reason: `Unknown screening id “${screeningId}” — not in the shipped catalog (${GENERATION_EQUIPMENT_CATALOG_VERSION}). New envelopes require a sourced entry in the shipped catalog.`,
      });
      continue;
    }
    if (doc.generationCatalogVersion !== GENERATION_EQUIPMENT_CATALOG_VERSION) {
      rejected.push({
        docId: doc.docId,
        reason: `Catalog version drift on “${screeningId}” (row ${String(doc.generationCatalogVersion ?? 'unversioned')}, shipped ${GENERATION_EQUIPMENT_CATALOG_VERSION}) — re-run the seed (npm run seed:oem-catalog).`,
      });
      continue;
    }
    admittedIds.add(screeningId);
  }

  // Shipped-catalog order, duplicates collapsed.
  const admitted = GENERATION_EQUIPMENT_CATALOG.filter((entry) => admittedIds.has(entry.catalogId));

  return { admitted, rejected, hiddenCount, authority: 'firestore' };
}
